import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLE_TYPES, ROOT_TEAM_ID } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { selectIsAllTeamsScope, useTeamStore } from '@/stores/team-store';

/**
 * Group Security v3 §12 — HOME PATH (Root, path=null) action lock.
 *
 * The backend blocks a NON-admin member with `canEditHomePath=false` from creating OR editing loose
 * items at the section Root (path=null) — see open-api principal.service.__pe. This context lets
 * the FE MIRROR that: a content page (Assets/Screens/Playlists/Schedules/Engage) wraps its subtree in
 * `RootActionLockProvider` with the computed lock, and `usePermissions` reads it — so every
 * `canCreate/canEdit/canDelete` consumer under that page (toolbars, item menus, bulk bars) is disabled
 * at Root with no per-site wiring. Non-content pages never provide the context ⇒ default `false` ⇒ no
 * effect. Admins (Owner/Admin/Super Admin/Team Owner) are never locked.
 */
const RootActionLockContext = createContext(false);

const ADMIN_ROLES: string[] = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.OWNER,
  ROLE_TYPES.TEAM_OWNER,
];

export function RootActionLockProvider({
  locked,
  children,
}: {
  locked: boolean;
  children: ReactNode;
}) {
  return <RootActionLockContext.Provider value={locked}>{children}</RootActionLockContext.Provider>;
}

/** Whether the current `usePermissions` consumer sits under a Root-locked content subtree. */
export function useRootActionLock(): boolean {
  return useContext(RootActionLockContext);
}

/**
 * Compute the Root (path=null) action lock for the CURRENT member — the value a content page passes to
 * `RootActionLockProvider`. Mirrors the backend §12 gate: locked ⇔ at Root AND a non-admin AND
 * `canEditHomePath !== true`. `atRoot` = the page is showing the section Root (no folder open).
 *
 * SCOPE WIDENED 2026-08-28 (Ted), in lockstep with principal.service.__pe. This used to also require
 * `me.groupIds.length > 0` ("backend only locks group members at Root"). That is no longer true: the
 * backend now binds every non-admin, so the old test left the mirror too permissive and the UI offered
 * New Folder / Create at Root to someone the API then refused — which is exactly what QA hit: the
 * folder dialog accepted the name, sent `saveGroup`, and the mutation came back PERMISSION_DENIED with
 * nothing shown to the user. A mirror that is more permissive than the gate produces silent failures,
 * so the two conditions must move together.
 */
export function useRootActionLockValue(atRoot: boolean): boolean {
  const me = useAuthStore((s) => s.me);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  if (!atRoot || !me) return false;
  const teamRole = me.teams?.find((t) => t._id === activeTeamId)?.role;
  if (ADMIN_ROLES.includes(teamRole ?? '')) return false; // Owner/Admin/Super Admin/Team Owner bypass
  // Two restrictions, OR'd — mirroring principal.service.__pe exactly:
  //   · a group member (the ORIGINAL rule, applies in every teamspace incl. Default), and
  //   · any non-admin in a REAL teamspace, where the "Members can create or edit at the section
  //     root" toggle lives.
  // ROOT/Default is excluded from the second because it has NO team document: it never appears on
  // the Teamspaces page and no toggle can re-enable it, so binding it would leave a brand-new
  // account's first non-admin unable to create at Home with nothing in the UI to fix it.
  const isRealTeamspace = !!activeTeamId && activeTeamId !== ROOT_TEAM_ID;
  const isGroupMember = (me.groupIds?.length ?? 0) > 0;
  if (!isGroupMember && !isRealTeamspace) return false;
  // #2246: the capability is PER TEAMSPACE. `canEditHomePath` is the account-wide answer; a
  // team-scoped grant, member row or toggle unlocks only the teamspace it belongs to, reported in
  // `canEditHomePathTeamIds`. Same OR the backend gate applies against `authContext.teamId`.
  if (me.canEditHomePath === true) return false;
  return !(me.canEditHomePathTeamIds ?? []).includes(activeTeamId);
}

/** The section Root as every create payload spells it: no path, `''`, or `'/'`. */
export function isRootPath(path: string | null | undefined): boolean {
  return !path || path === '/';
}

/**
 * The create-at-path guard a DIALOG owns for itself (#2138 follow-up, #2208).
 *
 * Triggers (toolbar buttons, pickers, search quick-creates) hide or disable themselves through the
 * page provider, but the dialog or hook that finally sends the mutation used to trust whoever
 * opened it — and every one of them opens through a shared store action any new caller can reach
 * without re-implementing the trigger's gate. That is exactly how the three #2138 gaps arose. A
 * dialog that reads its OWN target path here cannot be bypassed from the frontend by construction:
 * fold `reason` into `useDialogSubmitState({ validationError })`, or refuse up front in a hook.
 *
 * Two reasons a create at the Root is refused, checked in this order:
 *   1. All Teamspaces scope (#2208, spec §1d AV-2): at the Root no folder names a target
 *      teamspace, so the destination is ambiguous — "Select a teamspace to create". Inside a
 *      folder the folder's own teamId settles it, so this contributes nothing there (same rule as
 *      `useAllScopeCreateBlock`, which the trigger layer uses).
 *   2. §12 HOME PATH lock — same predicate as `useRootActionLockValue`.
 * `reason` is the one explanation every locked surface shows, so the copy cannot drift either.
 */
export function useRootCreateLock(targetPath: string | null | undefined): {
  locked: boolean;
  reason: string | undefined;
} {
  const { t } = useTranslation();
  const atRoot = isRootPath(targetPath);
  const allScopeBlocked = useTeamStore(selectIsAllTeamsScope) && atRoot;
  const homePathLocked = useRootActionLockValue(atRoot);
  if (allScopeBlocked) return { locked: true, reason: t('Select a teamspace to create') };
  if (homePathLocked) {
    return {
      locked: true,
      reason: t(
        'Creating at the home root is turned off for this teamspace. Open a folder to create there.',
      ),
    };
  }
  return { locked: false, reason: undefined };
}
